import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfigStore, DEFAULT_CONFIG } from '../../stores/configStore';
import { doc, getDoc, setDoc, getDocs, collection, query, where, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase/app';
import { useAuthStore } from '../../stores/authStore';
import { colors } from '../../styles/colors';
import type { AppConfig, BatteryTier, AdminEmailNotifications, CellPackProduct } from '../../types/config';

function PlatformBadge({ platform }: { platform: 'web' | 'mobile' | 'all' }) {
  const label = platform === 'web' ? '🌐 Web' : platform === 'mobile' ? '📱 Mobile' : '📡 All';
  const bg = platform === 'web' ? '#2563eb20' : platform === 'mobile' ? '#7c3aed20' : '#05966920';
  const fg = platform === 'web' ? '#60a5fa' : platform === 'mobile' ? '#a78bfa' : '#34d399';
  return (
    <span className="ml-2 rounded px-1.5 py-0.5 text-[9px] font-bold" style={{ backgroundColor: bg, color: fg }}>
      {label}
    </span>
  );
}

/* ─── Helpers ─── */

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-4 rounded-lg p-3" style={{ backgroundColor: colors.card, borderLeft: `3px solid ${colors.warning}` }}>
      <h3 className="mb-3 text-sm font-bold" style={{ color: colors.warning }}>{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, desc, children }: { label: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <div className="flex-1">
        <p className="text-xs font-semibold" style={{ color: colors.text }}>{label}</p>
        {desc && <p className="text-[10px]" style={{ color: colors.textMuted }}>{desc}</p>}
      </div>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, min, max, suffix }: {
  value: number; onChange: (v: number) => void;
  min?: number; max?: number; suffix?: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <input type="number" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min} max={max}
        className="w-24 rounded border px-2 py-1 text-right text-xs"
        style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
      {suffix && <span className="text-[10px]" style={{ color: colors.textMuted }}>{suffix}</span>}
    </div>
  );
}

function OptionButtons({ options, value, onChange }: {
  options: { key: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex gap-1">
      {options.map((o) => (
        <button key={o.key} onClick={() => onChange(o.key)}
          className="rounded px-2 py-1 text-[10px] font-semibold"
          style={{
            backgroundColor: value === o.key ? colors.warning : colors.surface,
            color: value === o.key ? '#000' : colors.textSecondary,
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AdminToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)}
      className="relative h-5 w-9 shrink-0 rounded-full transition-colors"
      style={{ backgroundColor: checked ? colors.warning : '#555' }}>
      <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform"
        style={{ transform: checked ? 'translateX(16px)' : 'translateX(0)' }} />
    </button>
  );
}

/**
 * 관리자 탈퇴 대행.
 * 사용자가 앱에서 직접 탈퇴하지 못할 때(권한 오류 등) 관리자가 대신 처리한다.
 * users 문서만 지우면 Auth 계정과 재가입 제한(deletedUsers)이 남지 않으므로,
 * 세 단계를 모두 수행하는 adminBatchDeleteUsers Cloud Function을 호출한다.
 */
const errMsg = (err: unknown) => (err instanceof Error ? err.message : String(err));

function AdminUserDeletion({ cooldownDays }: { cooldownDays: number }) {
  const myUid = useAuthStore((s) => s.user?.uid);
  const [input, setInput] = useState('');
  const [found, setFound] = useState<{ uid: string; email: string; nickname: string; provider: string; isAdmin: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');

  const lookup = async () => {
    const key = input.trim();
    if (!key) return;
    setBusy(true); setResult(''); setFound(null);
    try {
      // 이메일 우선, 없으면 uid로 직접 조회
      let snap: { id: string; data: () => Record<string, unknown> } | null = null;
      if (key.includes('@')) {
        const q = query(collection(db, 'users'), where('email', '==', key), limit(1));
        const res = await getDocs(q);
        if (!res.empty) snap = res.docs[0];
      } else {
        const d = await getDoc(doc(db, 'users', key));
        if (d.exists()) snap = d;
      }
      if (!snap) { setResult('오류: 해당 사용자를 찾을 수 없습니다'); return; }
      const data = snap.data();
      setFound({
        uid: snap.id,
        email: String(data.email ?? ''),
        nickname: String(data.nickname ?? ''),
        provider: String(data.signupProvider ?? (data.isGuest ? 'guest' : 'unknown')),
        isAdmin: data.isAdmin === true,
      });
    } catch (err) {
      setResult(`오류: ${errMsg(err)}`);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!found) return;
    if (found.uid === myUid) { alert('본인 계정은 여기서 삭제할 수 없습니다.'); return; }
    if (found.isAdmin) { alert('관리자 계정은 여기서 삭제할 수 없습니다.'); return; }
    if (!confirm(`다음 사용자를 탈퇴 처리합니다.\n\n닉네임: ${found.nickname}\n이메일: ${found.email}\nUID: ${found.uid}\n\n• deletedUsers에 보관 (재가입 제한 ${cooldownDays}일)\n• users 문서 삭제\n• Firebase Auth 계정 삭제\n\n되돌릴 수 없습니다. 진행하시겠습니까?`)) return;
    setBusy(true); setResult('');
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { app } = await import('../../lib/firebase/app');
      const fn = httpsCallable(getFunctions(app), 'adminBatchDeleteUsers');
      const res = await fn({ uids: [found.uid], reason: 'admin_on_behalf' });
      const data = res.data as { results: { uid: string; success: boolean; error?: string }[] };
      const r = data.results?.[0];
      if (r?.success) {
        setResult(`"${found.nickname}" 탈퇴 처리 완료 — Auth 삭제 · users 삭제 · deletedUsers 보관 (재가입 제한 ${cooldownDays}일)`);
        setFound(null); setInput('');
      } else {
        setResult(`오류: ${r?.error ?? '알 수 없는 실패'}`);
      }
    } catch (err) {
      setResult(`오류: ${errMsg(err)}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-lg p-3" style={{ backgroundColor: colors.surface }}>
      <p className="mb-1 text-[10px] font-bold" style={{ color: colors.textMuted }}>사용자 탈퇴 대행</p>
      <p className="mb-2 text-[10px]" style={{ color: colors.textMuted }}>
        앱에서 탈퇴가 안 되는 사용자를 관리자가 대신 처리합니다. 보관 → users 삭제 → Auth 삭제까지 한 번에 수행됩니다.
      </p>
      <div className="mb-2 flex gap-1">
        <input type="text" value={input}
          onChange={(e) => { setInput(e.target.value); setFound(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
          placeholder="이메일 또는 UID"
          className="flex-1 rounded border px-3 py-2 text-xs"
          style={{ backgroundColor: colors.card, color: colors.text, borderColor: colors.border }} />
        <button disabled={busy || !input.trim()} onClick={lookup}
          className="rounded px-3 py-2 text-xs font-bold disabled:opacity-50"
          style={{ backgroundColor: colors.warning, color: '#000' }}>
          조회
        </button>
      </div>
      {found && (
        <div className="mb-2 rounded p-2 text-xs" style={{ backgroundColor: colors.card, color: colors.text }}>
          <p><b>{found.nickname}</b>{found.isAdmin && <span className="ml-1 rounded px-1 text-[9px] font-bold" style={{ backgroundColor: colors.error, color: '#fff' }}>ADMIN</span>}</p>
          <p style={{ color: colors.textMuted }}>{found.email || '(이메일 없음)'} · {found.provider}</p>
          <p className="break-all text-[10px]" style={{ color: colors.textMuted }}>{found.uid}</p>
          <button disabled={busy || found.isAdmin || found.uid === myUid} onClick={remove}
            className="mt-2 w-full rounded py-2 text-xs font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: colors.error }}>
            {busy ? '처리 중...' : '🗑 탈퇴 처리'}
          </button>
        </div>
      )}
      {result && (
        <p className="text-xs font-bold" style={{ color: result.startsWith('오류') ? colors.error : colors.success }}>
          {result}
        </p>
      )}
    </div>
  );
}

/* ─── Main ─── */

export default function AdminPage() {
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const serverConfig = useConfigStore((s) => s.config);

  // Local editable copy
  const [cfg, setCfg] = useState<AppConfig>({ ...serverConfig });

  // Admin push state
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushLang, setPushLang] = useState<'all' | 'ko' | 'en'>('all');
  const [pushNickname, setPushNickname] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const [pushResult, setPushResult] = useState('');
  const [pushUrgent, setPushUrgent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setCfg({ ...serverConfig });
  }, [serverConfig]);

  const update = <K extends keyof AppConfig>(key: K, value: AppConfig[K]) => {
    setCfg((prev) => ({ ...prev, [key]: value }));
  };

  const updateTier = (index: number, field: keyof BatteryTier, value: number) => {
    setCfg((prev) => {
      const tiers = [...prev.batteryConsumption];
      tiers[index] = { ...tiers[index], [field]: value };
      return { ...prev, batteryConsumption: tiers };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'appConfig', 'main'), cfg);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('[Admin] Save failed:', err);
      alert('저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto" style={{ backgroundColor: colors.background }}>
      <div className="p-3 pb-8">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/settings" className="text-sm" style={{ color: colors.textSecondary }}>←</Link>
            <h1 className="text-lg font-bold" style={{ color: colors.warning }}>{t('admin.title')}</h1>
          </div>
          <button onClick={save} disabled={saving}
            className="rounded-lg px-4 py-1.5 text-xs font-bold text-black disabled:opacity-50"
            style={{ backgroundColor: saved ? colors.success : colors.warning }}>
            {saved ? '✓ 저장됨' : saving ? '저장 중...' : '저장'}
          </button>
        </div>

        {/* ── Battery Tiers ── */}
        <Section title="배터리 소모 설정 (메시지당, 거리별)">
          <div className="mb-2 grid grid-cols-4 gap-1 text-center text-[10px] font-bold" style={{ color: colors.textMuted }}>
            <span>#</span><span>반경 (m)</span><span>비용 (셀/msg)</span><span></span>
          </div>
          {cfg.batteryConsumption.map((tier, i) => (
            <div key={i} className="mb-1 grid grid-cols-4 items-center gap-1">
              <span className="text-center text-xs" style={{ color: colors.textMuted }}>{i + 1}</span>
              <input type="number" value={tier.radius}
                onChange={(e) => updateTier(i, 'radius', Number(e.target.value))}
                className="rounded border px-2 py-1 text-center text-xs"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <input type="number" value={tier.cost}
                onChange={(e) => updateTier(i, 'cost', Number(e.target.value))}
                className="rounded border px-2 py-1 text-center text-xs"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <button onClick={() => {
                setCfg(prev => ({ ...prev, batteryConsumption: prev.batteryConsumption.filter((_, idx) => idx !== i) }));
              }} className="text-center text-xs font-bold" style={{ color: colors.error }}>✕</button>
            </div>
          ))}
          <button onClick={() => {
            const last = cfg.batteryConsumption[cfg.batteryConsumption.length - 1];
            setCfg(prev => ({ ...prev, batteryConsumption: [...prev.batteryConsumption, { radius: (last?.radius ?? 100000) + 50000, cost: (last?.cost ?? 1000) + 500 }] }));
          }} className="mt-1 w-full rounded py-1.5 text-xs font-bold"
            style={{ backgroundColor: colors.card, color: colors.primary, border: `1px dashed ${colors.primary}40` }}>
            + 감도 단계 추가
          </button>
        </Section>

        {/* ── Message Costs ── */}
        <Section title="메시지 비용">
          <Field label="메시지 전송 비용">
            <NumInput value={cfg.messageSendCost}
              onChange={(v) => update('messageSendCost', v)} suffix="셀" />
          </Field>
          <Field label="쪽지(DM) 전송 비용">
            <NumInput value={cfg.dmSendCost}
              onChange={(v) => update('dmSendCost', v)} suffix="셀" />
          </Field>
          <Field label="닉네임 변경 비용">
            <NumInput value={cfg.nicknameChangeCost}
              onChange={(v) => update('nicknameChangeCost', v)} suffix="셀" />
          </Field>
        </Section>

        {/* ── Default Cells ── */}
        <Section title="가입 기본 셀">
          <Field label="일반 회원 기본 셀">
            <NumInput value={cfg.signupDefaultCells}
              onChange={(v) => update('signupDefaultCells', v)} suffix="셀" />
          </Field>
          <Field label="리워드 광고 충전량">
            <NumInput value={cfg.rewardAdAmount}
              onChange={(v) => update('rewardAdAmount', v)} suffix="셀" />
          </Field>
        </Section>

        {/* ── Auto Charge ── */}
        <Section title="자동 충전">
          <Field label="자동 충전 간격 (0=비활성)">
            <div className="flex items-center gap-1">
              <NumInput value={cfg.autoChargeIntervalMinutes < 60 ? cfg.autoChargeIntervalMinutes : Math.round(cfg.autoChargeIntervalMinutes / 60)}
                onChange={(v) => {
                  const unit = cfg.autoChargeIntervalMinutes >= 60 ? 60 : 1;
                  update('autoChargeIntervalMinutes', v * unit);
                }} suffix="" />
              <select value={cfg.autoChargeIntervalMinutes >= 60 ? 'hours' : 'minutes'}
                onChange={(e) => {
                  const cur = cfg.autoChargeIntervalMinutes;
                  if (e.target.value === 'hours') update('autoChargeIntervalMinutes', cur < 60 ? cur * 60 : cur);
                  else update('autoChargeIntervalMinutes', cur >= 60 ? Math.round(cur / 60) : cur);
                }}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #555', backgroundColor: '#222', color: '#eee', fontSize: 13 }}>
                <option value="minutes">분</option>
                <option value="hours">시간</option>
              </select>
            </div>
          </Field>
          <Field label="자동 충전 금액 (기본값 초과 불가)">
            <NumInput value={cfg.autoChargeAmount ?? 100000}
              onChange={(v) => update('autoChargeAmount', v)} suffix="셀" />
          </Field>
        </Section>

        {/* ── Manual Freq ── */}
        <Section title="위치 점프">
          <Field label="점프 비용 (1회)">
            <NumInput value={cfg.manualFreqMode.consumptionAmount}
              onChange={(v) => update('manualFreqMode', { ...cfg.manualFreqMode, consumptionAmount: v })}
              suffix="셀" />
          </Field>
          <Field label="점프 중 메시지 발송 비용">
            <NumInput value={cfg.manualFreqMode.jumpSendCost ?? 50}
              onChange={(v) => update('manualFreqMode', { ...cfg.manualFreqMode, jumpSendCost: v })}
              suffix="셀" />
          </Field>
        </Section>

        {/* ── Location Jump Permissions ── */}
        <Section title="위치 점프 권한">
          <Field label="관리자">
            <AdminToggle checked={cfg.locationJumpPermissions.admin}
              onChange={(v) => update('locationJumpPermissions', { ...cfg.locationJumpPermissions, admin: v })} />
          </Field>
          <Field label="운영자">
            <AdminToggle checked={cfg.locationJumpPermissions.operator}
              onChange={(v) => update('locationJumpPermissions', { ...cfg.locationJumpPermissions, operator: v })} />
          </Field>
          <Field label="일반 사용자">
            <AdminToggle checked={cfg.locationJumpPermissions.user}
              onChange={(v) => update('locationJumpPermissions', { ...cfg.locationJumpPermissions, user: v })} />
          </Field>
        </Section>

        {/* ── Map Long-Press Permissions ── */}
        <Section title="구글맵 길게 누르기 권한">
          <Field label="관리자">
            <AdminToggle checked={cfg.mapLongPressPermissions?.admin ?? true}
              onChange={(v) => update('mapLongPressPermissions', { ...(cfg.mapLongPressPermissions ?? { admin: true, operator: true, user: false }), admin: v })} />
          </Field>
          <Field label="운영자">
            <AdminToggle checked={cfg.mapLongPressPermissions?.operator ?? true}
              onChange={(v) => update('mapLongPressPermissions', { ...(cfg.mapLongPressPermissions ?? { admin: true, operator: true, user: false }), operator: v })} />
          </Field>
          <Field label="일반 사용자">
            <AdminToggle checked={cfg.mapLongPressPermissions?.user ?? false}
              onChange={(v) => update('mapLongPressPermissions', { ...(cfg.mapLongPressPermissions ?? { admin: true, operator: true, user: false }), user: v })} />
          </Field>
        </Section>

        {/* ── Location Drops ── */}
        <Section title="위치 메시지 드롭">
          <Field label="기본 비용" desc="드롭 시 기본 차감">
            <NumInput value={cfg.dropBaseCost}
              onChange={(v) => update('dropBaseCost', v)} suffix="셀" />
          </Field>
          <Field label="5분당 비용" desc="표시 시간에 따른 추가 비용">
            <NumInput value={cfg.dropCostPer5Min ?? Math.round((cfg.dropCostPerHour ?? 120000) / 12)}
              onChange={(v) => update('dropCostPer5Min' as any, v)} suffix="셀/5분" />
          </Field>
          <Field label="최대 유효 시간">
            <NumInput value={cfg.dropMaxDurationHours}
              onChange={(v) => update('dropMaxDurationHours', v)} suffix="시간" />
          </Field>
        </Section>

        {/* ── UI Settings ── */}
        <Section title="UI 설정">
          <Field label="주변 사용자 최대 수" desc="주변 사용자 탭과 레이더에 표시할 최대 인원">
            <NumInput value={cfg.nearbyUsersLimit}
              onChange={(v) => update('nearbyUsersLimit', v)} suffix="명" />
          </Field>
        </Section>

        {/* ── Notification Settings (Common) ── */}
        <Section title={<>알림 설정<PlatformBadge platform="all" /></>}>
          <Field label="접속 알림 활성화">
            <AdminToggle checked={cfg.nearbyUserNotification.enabled}
              onChange={(v) => update('nearbyUserNotification', { ...cfg.nearbyUserNotification, enabled: v })} />
          </Field>
          <Field label="접속 알림 간격" desc="동일 사용자 접속 알림 최소 간격">
            <NumInput value={cfg.nearbyUserNotification.defaultIntervalMinutes}
              onChange={(v) => update('nearbyUserNotification', { ...cfg.nearbyUserNotification, defaultIntervalMinutes: v })}
              suffix="분" />
          </Field>
        </Section>

        {/* ── Notification Settings (Mobile) ── removed: server push toggle (always on) and active threshold (auto-detected via appStateChange) */}

        {/* ── Bot ── */}
        <Section title="AI Bot">
          <Field label="Bot 로고 표시" desc="비활성 시 봇이 일반 사용자처럼 표시됨 (BOT 배지, B 아이콘 숨김)">
            <AdminToggle checked={cfg.showBotBadge ?? true}
              onChange={(v) => update('showBotBadge', v)} />
          </Field>
        </Section>

        {/* ── Account ── */}
        <Section title="계정 관리">
          <Field label="탈퇴 후 재가입 제한 기간" desc="탈퇴 후 이 기간 동안 동일 계정으로 재가입 불가">
            <NumInput value={cfg.deletionCooldownDays ?? 7}
              onChange={(v) => update('deletionCooldownDays', v)} suffix="일" />
          </Field>
          <AdminUserDeletion cooldownDays={serverConfig.deletionCooldownDays ?? 7} />
        </Section>

        {/* ── Map Consent Flow (per platform) ── */}
        <Section title={<>지도 표시 동의 플로우<PlatformBadge platform="all" /></>}>
          <p className="mb-2 text-[10px] leading-relaxed" style={{ color: colors.textMuted }}>
            플랫폼별 on/off. <b>ON(엄격)</b>=기본 "숨김" + 로그인마다 "지도에 표시할까요?" 팝업, 설정 토글 노출.<br/>
            <b>OFF(완화)</b>=항상 지도에 표시, 설정 토글·팝업 모두 숨김.
          </p>
          {(['web', 'android', 'ios'] as const).map((p) => (
            <Field key={p} label={p.toUpperCase()}>
              <AdminToggle
                checked={(cfg.mapConsentRequiredPlatforms ?? { web: true, android: true, ios: true })[p] !== false}
                onChange={(v) => update('mapConsentRequiredPlatforms', {
                  ...(cfg.mapConsentRequiredPlatforms ?? { web: true, android: true, ios: true }),
                  [p]: v,
                })}
              />
            </Field>
          ))}
        </Section>

        {/* ── Monetization (Mobile) ── */}
        <Section title={<>결제/광고<PlatformBadge platform="mobile" /></>}>
          <Field label="셀 구매 버튼 표시 (인앱 결제)" desc="모바일 앱에서 인앱 결제 버튼 노출">
            <AdminToggle checked={cfg.showCellPurchaseButton}
              onChange={(v) => update('showCellPurchaseButton', v)} />
          </Field>
          <Field label="리워드 광고 버튼 표시 (보상형 광고)" desc="모바일 앱에서 보상형 광고 시청 버튼 노출">
            <AdminToggle checked={cfg.showRewardAdButton}
              onChange={(v) => update('showRewardAdButton', v)} />
          </Field>
        </Section>

        {/* ── Cell Pack Products ── */}
        <Section title={<>셀팩 상품 관리<PlatformBadge platform="mobile" /></>}>
          <p className="mb-2 text-[10px]" style={{ color: colors.textMuted }}>
            스토어 가격은 Google Play / App Store에서 직접 변경. 여기서는 이름, 지급 셀량, 스토어 제품 ID를 관리합니다.
          </p>
          <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-bold" style={{ color: colors.textMuted }}>
            <span>제품 ID</span><span>Android ID</span><span>iOS ID</span><span>지급 셀</span><span>한국어 이름</span><span>English</span><span></span>
          </div>
          {(cfg.cellPacks ?? []).map((pack, i) => (
            <div key={i} className="mb-1.5 grid grid-cols-7 items-center gap-1">
              <input type="text" value={pack.id} placeholder="id"
                onChange={(e) => {
                  const packs = [...(cfg.cellPacks ?? [])];
                  packs[i] = { ...packs[i], id: e.target.value };
                  update('cellPacks', packs);
                }}
                className="rounded border px-1.5 py-1 text-center text-[10px]"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <input type="text" value={pack.androidId} placeholder="android"
                onChange={(e) => {
                  const packs = [...(cfg.cellPacks ?? [])];
                  packs[i] = { ...packs[i], androidId: e.target.value };
                  update('cellPacks', packs);
                }}
                className="rounded border px-1.5 py-1 text-center text-[10px]"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <input type="text" value={pack.iosId} placeholder="ios"
                onChange={(e) => {
                  const packs = [...(cfg.cellPacks ?? [])];
                  packs[i] = { ...packs[i], iosId: e.target.value };
                  update('cellPacks', packs);
                }}
                className="rounded border px-1.5 py-1 text-center text-[10px]"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <input type="number" value={pack.cells} placeholder="셀"
                onChange={(e) => {
                  const packs = [...(cfg.cellPacks ?? [])];
                  packs[i] = { ...packs[i], cells: Number(e.target.value) };
                  update('cellPacks', packs);
                }}
                className="rounded border px-1.5 py-1 text-center text-[10px]"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <input type="text" value={pack.label.ko} placeholder="한국어"
                onChange={(e) => {
                  const packs = [...(cfg.cellPacks ?? [])];
                  packs[i] = { ...packs[i], label: { ...packs[i].label, ko: e.target.value } };
                  update('cellPacks', packs);
                }}
                className="rounded border px-1.5 py-1 text-center text-[10px]"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <input type="text" value={pack.label.en} placeholder="English"
                onChange={(e) => {
                  const packs = [...(cfg.cellPacks ?? [])];
                  packs[i] = { ...packs[i], label: { ...packs[i].label, en: e.target.value } };
                  update('cellPacks', packs);
                }}
                className="rounded border px-1.5 py-1 text-center text-[10px]"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <button onClick={() => {
                if (!confirm(`"${pack.label.ko || pack.id}" 셀팩을 삭제하시겠습니까?`)) return;
                update('cellPacks', (cfg.cellPacks ?? []).filter((_, idx) => idx !== i));
              }} className="text-center text-xs font-bold" style={{ color: colors.error }}>✕</button>
            </div>
          ))}
          <button onClick={() => {
            const newPack: CellPackProduct = { id: '', androidId: '', iosId: '', cells: 0, label: { ko: '', en: '' } };
            update('cellPacks', [...(cfg.cellPacks ?? []), newPack]);
          }} className="mt-1 w-full rounded py-1.5 text-xs font-bold"
            style={{ backgroundColor: colors.card, color: colors.primary, border: `1px dashed ${colors.primary}40` }}>
            + 셀팩 추가
          </button>
        </Section>

        {/* ── Mobile App Version ── */}
        <Section title={<>모바일 앱 버전 관리<PlatformBadge platform="mobile" /></>}>
          <Field label="Android 최소 버전" desc="이 버전 미만의 앱은 강제 업데이트 표시">
            <input type="text" value={cfg.minAppVersion?.android ?? ''}
              onChange={(e) => update('minAppVersion', { ...(cfg.minAppVersion ?? { android: '', ios: '' }), android: e.target.value })}
              placeholder="예: 2.0.0"
              className="w-24 rounded border px-2 py-1 text-right text-xs"
              style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
          </Field>
          <Field label="iOS 최소 버전" desc="이 버전 미만의 앱은 강제 업데이트 표시">
            <input type="text" value={cfg.minAppVersion?.ios ?? ''}
              onChange={(e) => update('minAppVersion', { ...(cfg.minAppVersion ?? { android: '', ios: '' }), ios: e.target.value })}
              placeholder="예: 2.0.0"
              className="w-24 rounded border px-2 py-1 text-right text-xs"
              style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
          </Field>
        </Section>

        {/* ── Admin Email Notification Settings ── */}
        <Section title="관리자 이메일 알림">
          <Field label="이메일 알림 활성화" desc="관리자에게 이메일 알림을 발송합니다">
            <AdminToggle checked={cfg.adminEmailNotifications?.enabled ?? false}
              onChange={(v) => update('adminEmailNotifications', { ...(cfg.adminEmailNotifications ?? { enabled: false, recipientEmail: '', onNewUser: true, onUserDeletion: true, onReport: true, onNewPost: true }), enabled: v })} />
          </Field>
          {cfg.adminEmailNotifications?.enabled && (
            <>
              <Field label="수신 이메일" desc="알림을 받을 관리자 이메일 주소">
                <input type="email" value={cfg.adminEmailNotifications?.recipientEmail ?? ''}
                  onChange={(e) => update('adminEmailNotifications', { ...cfg.adminEmailNotifications!, recipientEmail: e.target.value })}
                  placeholder="admin@ourhertz.com"
                  className="w-48 rounded border px-2 py-1 text-xs"
                  style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              </Field>
              <Field label="신규 회원 가입" desc="새 회원 가입 시 이메일 알림">
                <AdminToggle checked={cfg.adminEmailNotifications?.onNewUser ?? true}
                  onChange={(v) => update('adminEmailNotifications', { ...cfg.adminEmailNotifications!, onNewUser: v })} />
              </Field>
              <Field label="회원 탈퇴" desc="회원 탈퇴 시 이메일 알림">
                <AdminToggle checked={cfg.adminEmailNotifications?.onUserDeletion ?? true}
                  onChange={(v) => update('adminEmailNotifications', { ...cfg.adminEmailNotifications!, onUserDeletion: v })} />
              </Field>
              <Field label="신고 접수" desc="새 신고 접수 시 이메일 알림">
                <AdminToggle checked={cfg.adminEmailNotifications?.onReport ?? true}
                  onChange={(v) => update('adminEmailNotifications', { ...cfg.adminEmailNotifications!, onReport: v })} />
              </Field>
              <Field label="게시물 등록" desc="새 게시물 등록 시 이메일 알림">
                <AdminToggle checked={cfg.adminEmailNotifications?.onNewPost ?? true}
                  onChange={(v) => update('adminEmailNotifications', { ...cfg.adminEmailNotifications!, onNewPost: v })} />
              </Field>
            </>
          )}
        </Section>

        {/* ── Admin Push Notification ── */}
        <Section title="관리자 푸시 발송">
          {/* 제목 */}
          <div className="mb-2">
            <label className="mb-1 block text-[10px] font-bold" style={{ color: colors.textMuted }}>알림 제목</label>
            <input type="text" value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              placeholder="알림 제목을 입력하세요"
              className="w-full rounded border px-3 py-2 text-xs"
              style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
          </div>

          {/* 내용 */}
          <div className="mb-3">
            <label className="mb-1 block text-[10px] font-bold" style={{ color: colors.textMuted }}>알림 내용</label>
            <textarea value={pushBody}
              onChange={(e) => setPushBody(e.target.value)}
              placeholder="알림 내용을 입력하세요"
              rows={3}
              className="w-full resize-y rounded border px-3 py-2 text-xs leading-relaxed"
              style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
          </div>

          {/* 긴급 공지 체크박스 */}
          <label className="mb-3 flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={pushUrgent} onChange={(e) => setPushUrgent(e.target.checked)}
              className="h-4 w-4 rounded" />
            <span className="text-xs font-bold" style={{ color: colors.error }}>
              긴급 공지 (서버 알림 OFF 사용자에게도 발송)
            </span>
          </label>

          {/* Broadcast to all or by language */}
          <div className="mb-3 rounded-lg p-3" style={{ backgroundColor: colors.surface }}>
            <p className="mb-2 text-[10px] font-bold" style={{ color: colors.textMuted }}>전체 발송</p>
            <div className="mb-2">
              <OptionButtons
                options={[
                  { key: 'all', label: '전체' },
                  { key: 'ko', label: '한국어' },
                  { key: 'en', label: 'English' },
                ]}
                value={pushLang}
                onChange={(v) => setPushLang(v as any)}
              />
            </div>
            <button
              disabled={pushSending || !pushTitle || !pushBody}
              onClick={async () => {
                const target = pushLang === 'all' ? '전체' : pushLang === 'ko' ? '한국어' : 'English';
                if (!confirm(`[${target}] 사용자에게 푸시를 발송합니다.\n\n제목: ${pushTitle}\n내용: ${pushBody}\n긴급: ${pushUrgent ? 'YES' : 'NO'}\n\n발송하시겠습니까?`)) return;
                setPushSending(true); setPushResult('');
                try {
                  const { getFunctions, httpsCallable } = await import('firebase/functions');
                  const { app } = await import('../../lib/firebase/app');
                  const fns = getFunctions(app);
                  const fn = httpsCallable(fns, 'sendAdminPush');
                  const res = await fn({ title: pushTitle, body: pushBody, language: pushLang === 'all' ? undefined : pushLang, urgent: pushUrgent });
                  const data = res.data as any;
                  setPushResult(`발송: ${data.sent}명, 실패: ${data.failed}명`);
                } catch (err: any) {
                  setPushResult(`오류: ${err.message}`);
                } finally { setPushSending(false); }
              }}
              className="w-full rounded py-2 text-xs font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: colors.error }}>
              {pushSending ? '발송 중...' : `📢 ${pushLang === 'all' ? '전체' : pushLang} 사용자에게 발송`}
            </button>
          </div>

          {/* Send to specific user */}
          <div className="rounded-lg p-3" style={{ backgroundColor: colors.surface }}>
            <p className="mb-2 text-[10px] font-bold" style={{ color: colors.textMuted }}>특정 사용자 발송</p>
            <input type="text" value={pushNickname}
              onChange={(e) => setPushNickname(e.target.value)}
              placeholder="닉네임을 입력하세요"
              className="mb-2 w-full rounded border px-3 py-2 text-xs"
              style={{ backgroundColor: colors.card, color: colors.text, borderColor: colors.border }} />
            <button
              disabled={pushSending || !pushTitle || !pushBody || !pushNickname}
              onClick={async () => {
                if (!confirm(`"${pushNickname}"에게 푸시를 발송합니다.\n\n제목: ${pushTitle}\n내용: ${pushBody}\n긴급: ${pushUrgent ? 'YES' : 'NO'}\n\n발송하시겠습니까?`)) return;
                setPushSending(true); setPushResult('');
                try {
                  const { getFunctions, httpsCallable } = await import('firebase/functions');
                  const { app } = await import('../../lib/firebase/app');
                  const fns = getFunctions(app);
                  const fn = httpsCallable(fns, 'sendAdminPushToUser');
                  const res = await fn({ title: pushTitle, body: pushBody, nickname: pushNickname, urgent: pushUrgent });
                  const data = res.data as any;
                  setPushResult(`"${data.nickname}"에게 발송 완료`);
                } catch (err: any) {
                  setPushResult(`오류: ${err.message}`);
                } finally { setPushSending(false); }
              }}
              className="w-full rounded py-2 text-xs font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: colors.primary }}>
              {pushSending ? '발송 중...' : '📨 발송'}
            </button>
          </div>

          {pushResult && (
            <p className="mt-2 text-xs font-bold" style={{ color: pushResult.startsWith('오류') ? colors.error : colors.success }}>
              {pushResult}
            </p>
          )}
        </Section>

      </div>
    </div>
  );
}
